import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import UserAvatar from "./UserAvatar";
import { MESSAGING_ENABLED } from "../config/messagingEnabled";
import { useMessages } from "../messages/integration";
import { openAdminPanel } from "../utils/adminPanel";
import fishmapIcon from "../assets/icon-main.png";

const RAW_API_BASE = process.env.REACT_APP_API_URL || "";
const API_BASE = RAW_API_BASE.replace(/\/+$/, "").replace(/\/api$/, "");

const NAV_LINKS = [
    { to: "/map", label: "Карта" },
    { to: "/fishes", label: "Рыбы" },
    { to: "/blog", label: "Блог" },
    { to: "/tools", label: "Инструменты" },
    { to: "/spots/new", label: "Добавить точку"},
];

function safeInternalPath(next) {
    if (typeof next !== "string" || !next.startsWith("/") || next.startsWith("//")) {
        return null;
    }
    return next;
}

export default function Header() {
    const { user, ready, isAuthenticated, logout, loginWithToken } = useAuth();
    const { unreadCount } = useMessages();
    const [loginModalOpen, setLoginModalOpen] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [navOpen, setNavOpen] = useState(false);
    const menuRef = useRef(null);
    const location = useLocation();

    useEffect(() => {
        if (!menuOpen) return undefined;
        const onDocumentClick = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", onDocumentClick);

        return () => document.removeEventListener("mousedown", onDocumentClick);
    }, [menuOpen]);

    useEffect(() => {
        setNavOpen(false);
        setMenuOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (!navOpen) return undefined;
        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                setNavOpen(false);
            }
        };

        document.addEventListener("keydown", onKeyDown);

        return () => document.removeEventListener("keydown", onKeyDown);
    }, [navOpen]);

    const [identifier, setIdentifier] = useState("");
    const [password, setPassword] = useState("");
    const [message, setMessage] = useState("");
    const [emailVerificationRequired, setEmailVerificationRequired] = useState(false);
    const [resendingVerification, setResendingVerification] = useState(false);
    const [loginNextPath, setLoginNextPath] = useState(null);
    const [loginModalTitle, setLoginModalTitle] = useState("Вход");
    const navigate = useNavigate();

    const userDisplayName = useMemo(
        () => user?.name || user?.username || (user?.email ? user.email.split("@")[0] : "Пользователь"),
        [user],
    );

    const isAdmin = Boolean(user?.isAdmin);

    useEffect(() => {
        const onOpenAuthModal = (event) => {
            const nextPath = safeInternalPath(event?.detail?.nextPath || "");
            const title = String(event?.detail?.title || "").trim();
            setLoginNextPath(nextPath);
            setLoginModalTitle(title || "Вход");
            setMessage("");
            setEmailVerificationRequired(false);
            setLoginModalOpen(true);
        };
        window.addEventListener("auth:open-login-modal", onOpenAuthModal);

        return () => window.removeEventListener("auth:open-login-modal", onOpenAuthModal);
    }, []);

    async function onLoginSubmit(event) {
        event.preventDefault();
        setMessage("");
        setEmailVerificationRequired(false);
        const res = await fetch(`${API_BASE}/api/login`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ email: identifier, password }),
        });

        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            const backendMessage = String(data?.message || data?.error || "").trim();
            const isInvalidCredentials = backendMessage.toLowerCase() === "invalid credentials.";
            const isEmailNotVerified = backendMessage.toLowerCase().includes("email not verified");
            if (isEmailNotVerified) {
                setEmailVerificationRequired(true);
                setMessage("Подтвердите email перед входом. Проверьте письмо со ссылкой подтверждения.");
                return;
            }

            setMessage(
                isInvalidCredentials
                    ? "Неверный логин или пароль"
                    : (backendMessage || "Неверный Email или пароль")
            );

            return;
        }
        if (!data.token) {
            setMessage("Ответ сервера без токена");

            return;
        }
        await loginWithToken(data.token, data.refresh_token || "");
        setIdentifier("");
        setPassword("");
        setLoginModalOpen(false);
        if (loginNextPath) {
            navigate(loginNextPath, { replace: true });
            setLoginNextPath(null);
        }
    }

    async function resendVerificationEmail() {
        const email = identifier.trim();
        if (!email) {
            setMessage("Укажите email, чтобы отправить письмо повторно.");

            return;
        }

        setResendingVerification(true);
        try {
            const res = await fetch(`${API_BASE}/api/verify/resend`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ email }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setMessage(data.error || data.message || "Не удалось отправить письмо подтверждения.");

                return;
            }
            setMessage(data.message || "Письмо подтверждения отправлено повторно.");
        } catch {
            setMessage("Ошибка сети");
        } finally {
            setResendingVerification(false);
        }
    }

    return (
        <>
            <header className="header-shell">
                <div className="header-top">
                    <Link className="brand" to="/">
                        <img
                            src={fishmapIcon}
                            alt="FishMap"
                            width={90}
                            height={60}
                            decoding="async"
                        />
                    </Link>
                    <button
                        type="button"
                        className="nav-drawer-toggle"
                        onClick={() => setNavOpen(true)}
                        aria-expanded={navOpen}
                        aria-controls="siteNav"
                    >
                        <span aria-hidden>☰</span>
                    </button>
                    <nav className={`header-nav nav-bar${navOpen ? " is-open" : ""}`} id="siteNav" aria-label="Основное меню">
                        <div className="nav-drawer-head">
                            <button
                                type="button"
                                className="nav-drawer-close"
                                onClick={() => setNavOpen(false)}
                                aria-label="Закрыть меню"
                            >
                                <span aria-hidden>×</span>
                            </button>
                        </div>
                        {NAV_LINKS.map((link) => (
                            <NavLink
                                key={link.to}
                                className={({ isActive }) => `${isActive ? " is-active" : ""}`.trim()}
                                to={link.to}
                                onClick={() => setNavOpen(false)}
                            >
                                {link.label}
                            </NavLink>
                        ))}
                    </nav>
                    <div className="header-top__right">
                    <div className="auth-buttons">
                        {!ready ? (
                            <span className="auth-buttons__placeholder" aria-hidden />
                        ) : isAuthenticated ? (
                            <>
                            {MESSAGING_ENABLED && <Link to="/messages" className="btn-outline pm-header-btn" title="Личные сообщения">
                                💬
                                {unreadCount > 0 && <span className="pm-badge">{unreadCount > 99 ? "99+" : unreadCount}</span>}
                            </Link>}
                            <div className="menu-anchor" ref={menuRef}>
                                <button
                                    type="button"
                                    className="user-chip"
                                    onClick={() => setMenuOpen((v) => !v)}
                                >
                                    <UserAvatar
                                        avatarUrl={user?.avatarUrl}
                                        name={userDisplayName}
                                        className="user-chip__avatar"
                                    />
                                    <span className="user-chip__name">{userDisplayName}</span>
                                    <svg
                                        aria-hidden
                                        className="caret"
                                        width="18" height="18" viewBox="0 0 18 18" fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        style={{ transition: 'transform 0.2s', transform: menuOpen ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0 }}
                                    >
                                        <path d="M4.5 6.75L9 11.25L13.5 6.75" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </button>
                                {menuOpen && (
                                    <div className="user-menu">
                                        <Link to="/profile" onClick={() => setMenuOpen(false)}>Профиль</Link>
                                        <Link to="/profile/my-spots" onClick={() => setMenuOpen(false)}>Мои точки</Link>
                                        <Link to="/profile/favorites" onClick={() => setMenuOpen(false)}>Избранные точки</Link>
                                        <Link to="/profile/likes" onClick={() => setMenuOpen(false)}>Лайки</Link>
                                        <Link to="/profile/comments" onClick={() => setMenuOpen(false)}>Отчёты</Link>
                                        <Link to="/profile/blog-comments" onClick={() => setMenuOpen(false)}>Комментарии</Link>
                                        <Link to="/profile/suggestions" onClick={() => setMenuOpen(false)}>Предложения</Link>
                                        {isAdmin && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setMenuOpen(false);
                                                    openAdminPanel();
                                                }}
                                            >
                                                Админка
                                            </button>
                                        )}
                                        <button
                                            type="button"
                                            className="user-menu__logout"
                                            onClick={() => {
                                                setMenuOpen(false);
                                                logout();
                                            }}
                                        >
                                            Выйти
                                        </button>
                                    </div>
                                )}
                            </div>
                            </>
                        ) : (
                            <button
                                type="button"
                                className="btn-outline"
                                onClick={() => {
                                    setMessage("");
                                    setEmailVerificationRequired(false);
                                    setLoginNextPath(null);
                                    setLoginModalTitle("Вход");
                                    setLoginModalOpen(true);
                                }}
                            >
                                Войти
                            </button>
                        )}
                    </div>
                    </div>
                </div>
                {navOpen &&
                    <button
                        type="button"
                        className="nav-drawer-backdrop"
                        onClick={() => setNavOpen(false)} aria-label="Закрыть меню"
                    />
                }
            </header>
            {loginModalOpen && (
                <div className="auth-modal-backdrop" onClick={() => setLoginModalOpen(false)}>
                    <div className="auth-modal" onClick={(event) => event.stopPropagation()}>
                        <button
                            type="button"
                            className="entity-popup__close"
                            onClick={() => setLoginModalOpen(false)}
                            aria-label="Закрыть"
                        >
                            ×
                        </button>
                        <h3>{loginModalTitle}</h3>
                        {message && <p className="form-message">{message}</p>}
                        <form onSubmit={onLoginSubmit} className="auth-form">
                            <input
                                placeholder="Email"
                                type="text"
                                value={identifier}
                                onChange={(e) => {
                                    setIdentifier(e.target.value);
                                    setEmailVerificationRequired(false);
                                }}
                                required
                            />
                            <input
                                placeholder="Пароль"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                            />
                            <button className="toolbar-btn" type="submit">Войти</button>
                        </form>
                        <div className="auth-links">
                            <Link to="/register" className="btn-outline" onClick={() => setLoginModalOpen(false)}>Регистрация</Link>
                            <Link to="/forgot" className="btn-outline" onClick={() => setLoginModalOpen(false)}>Забыли пароль?</Link>
                        </div>
                        {emailVerificationRequired && (
                            <button type="button" className="auth-resend-link" onClick={resendVerificationEmail} disabled={resendingVerification}>
                                {resendingVerification ? "Отправляем..." : "Отправить email повторно"}
                            </button>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
